import { fetchJson } from "../lib/api";
import type { ProjectSpendMonth } from "./projects";

export type CostCenterType =
  | "project"
  | "delivery_address"
  | "customer_reference"
  | "order_context"
  | "manual_cost_center";

export type CostCenterEntity = {
  cost_center_type: CostCenterType | string;
  cost_center_key: string;
  display_label: string | null;
  source_display_label?: string | null;
  manual_display_label?: string | null;
  customer_anchor_key?: string | null;
  customer_anchor_label?: string | null;
  customer_anchor_reason?: string | null;
  project_role?: ManualProjectRole | string | null;
  status?: "active" | "archived" | string | null;
  source_observation_count: number | null;
  line_observation_count: number | null;
  document_count: number | null;
  line_count: number | null;
  distinct_article_count?: number | null;
  user_count: number | null;
  first_observed_date: string | null;
  last_observed_date: string | null;
  total_spend: string | number | null;
  spend_ytd: string | number | null;
  spend_last_year: string | number | null;
  spend_last30: string | number | null;
  credit_spend?: string | number | null;
  credit_spend_last_year?: string | number | null;
  source_kinds: string | null;
  confidence_labels: string | null;
  has_pdf_evidence: boolean | null;
  has_ugl_evidence: boolean | null;
  has_manual_evidence: boolean | null;
  member_aggregate_spend?: string | number | null;
  member_aggregate_spend_last_year?: string | number | null;
};

export type CostCenterCoverageStatus =
  | "complete"
  | "review_needed"
  | "source_gap"
  | "unsupported"
  | "unknown";

export type CostCenterSourceCoverage = {
  customer_id: string | null;
  domain: "cost_center" | string;
  row_kind?: "cost_center" | "customer_anchor" | string | null;
  entity_key: string;
  entity_type: CostCenterType | string;
  display_label: string | null;
  source_display_label?: string | null;
  manual_display_label?: string | null;
  customer_anchor_key?: string | null;
  customer_anchor_label?: string | null;
  customer_anchor_reason?: string | null;
  project_role?: ManualProjectRole | string | null;
  total_spend: string | number | null;
  spend_ytd?: string | number | null;
  spend_last_year?: string | number | null;
  spend_last30?: string | number | null;
  credit_spend?: string | number | null;
  credit_spend_last_year?: string | number | null;
  source_observation_count: number | string | null;
  line_observation_count: number | string | null;
  document_count: number | string | null;
  line_count: number | string | null;
  user_count: number | string | null;
  last_observed_date: string | null;
  has_pdf_evidence: boolean | null;
  has_ugl_evidence: boolean | null;
  has_datanorm_evidence: boolean | null;
  has_manual_evidence: boolean | null;
  pdf_document_count: number | string | null;
  pdf_line_count: number | string | null;
  ugl_document_count: number | string | null;
  ugl_line_count: number | string | null;
  datanorm_catalog_count: number | string | null;
  datanorm_set_count: number | string | null;
  manual_decision_count: number | string | null;
  matched_link_count: number | string | null;
  relationship_candidate_count: number | string | null;
  open_relationship_count: number | string | null;
  confirmed_relationship_count: number | string | null;
  rejected_relationship_count: number | string | null;
  coverage_status: CostCenterCoverageStatus | string;
  gap_codes: string | null;
  unsupported_source_codes: string | null;
  confidence_label: string | null;
  raw_reference_count?: number | string | null;
  raw_reference_limit?: number | string | null;
  raw_reference_has_more?: boolean | null;
  raw_references?: CostCenterAnchorRawReference[];
};

export type CostCenterAnchorRawReference = {
  entity_key: string;
  display_label: string | null;
  source_display_label?: string | null;
  manual_display_label?: string | null;
  total_spend: string | number | null;
  spend_ytd?: string | number | null;
  spend_last_year?: string | number | null;
  spend_last30?: string | number | null;
  credit_spend?: string | number | null;
  credit_spend_last_year?: string | number | null;
  document_count: string | number | null;
  line_count: string | number | null;
  pdf_document_count?: string | number | null;
  pdf_line_count?: string | number | null;
  last_observed_date: string | null;
  has_manual_evidence?: boolean | null;
};

export type CostCenterLabelCandidateEvidence = {
  source_kind: string;
  source_doc_id: string | null;
  source_doc_number: string | null;
  source_date: string | null;
  source_field: string | null;
  raw_value: string | null;
  source_locator: string | null;
};

export type CostCenterLabelCandidate = {
  label: string;
  source_kind: string;
  document_count: number;
  code_document_count: number;
  evidence_count: number;
  sample_values: string[];
  evidence: CostCenterLabelCandidateEvidence[];
  is_eligible: boolean;
  is_auto_label: boolean;
  auto_label_status: string | null;
  rejection_reason: string | null;
};

export type CostCenterLabelCandidatesResponse = {
  entity_type: CostCenterType | string;
  entity_key: string;
  display_label: string | null;
  source_display_label: string | null;
  manual_display_label: string | null;
  project_code: string | null;
  auto_label: string | null;
  auto_label_source_kind: string | null;
  auto_label_status: string | null;
  thresholds: {
    min_document_count: number;
    min_code_document_count: number;
    dominance_ratio: number;
  };
  candidates: CostCenterLabelCandidate[];
};

export type CostCenterSortMode =
  | "spend_desc"
  | "spend_ytd_desc"
  | "spend_last_year_desc"
  | "spend_last30_desc"
  | "recent_desc"
  | "documents_desc"
  | "lines_desc"
  | "name_asc";

export type CostCenterRelationshipCandidate = {
  parent_cost_center_key: string;
  parent_cost_center_type: CostCenterType | string;
  parent_display_label: string | null;
  parent_source_display_label?: string | null;
  parent_manual_display_label?: string | null;
  child_cost_center_key: string;
  child_cost_center_type: CostCenterType | string;
  child_display_label: string | null;
  child_source_display_label?: string | null;
  child_manual_display_label?: string | null;
  relationship_type: string;
  state: string;
  confidence: string | null;
  reason: string | null;
  evidence_count: number | null;
  document_count: number | null;
  line_count: number | null;
  score?: number | null;
  total_spend: string | number | null;
  first_observed_date: string | null;
  last_observed_date: string | null;
  source_kinds: string | null;
  source_summary: string | null;
  decision_action?: CostCenterRelationshipAuditAction | null;
  decision_actor_user_id?: string | null;
  decision_created_at?: string | null;
  decision_reason?: string | null;
  decision_event_id?: string | null;
};

export type CostCenterRelationshipEvidence = {
  parent_cost_center_key: string;
  parent_cost_center_type: CostCenterType | string;
  child_cost_center_key: string;
  child_cost_center_type: CostCenterType | string;
  relationship_type: string;
  confidence: string | null;
  reason: string | null;
  source_kind: string | null;
  source_doc_id: string | null;
  source_doc_number: string | null;
  source_line_pos: string | number | null;
  source_date: string | null;
  evidence_spend: string | number | null;
  source_summary: string | null;
};

export type CostCenterRelationshipDecisionAction =
  | "confirm"
  | "reject"
  | "reset";
export type CostCenterRelationshipAuditAction =
  | CostCenterRelationshipDecisionAction
  | "create"
  | "remove";
export type CostCenterRelationshipSortMode = "score_desc" | "spend_desc";
export type CostCenterRelationshipStatusFilter =
  | "all"
  | "open"
  | "auto"
  | "confirmed"
  | "rejected";
export type CostCenterRelationshipSourceFilter =
  | "all"
  | "pdf"
  | "ugl"
  | "manual";
export type CostCenterCoverageSourceFilter =
  | "all"
  | "pdf"
  | "ugl"
  | "datanorm"
  | "manual";

export type CostCenterRelationshipStatusStats = {
  all: number;
  open: number;
  auto: number;
  confirmed: number;
  rejected: number;
};

export type CostCenterRelationshipDecisionResponse = {
  relationship: CostCenterRelationshipCandidate;
  decision: {
    event_id: string;
    customer_id: string;
    relationship_type: string;
    parent_cost_center_key: string;
    child_cost_center_key: string;
    action: CostCenterRelationshipAuditAction;
    previous_state: string;
    new_state: string;
    reason: string | null;
    actor_user_id: string;
    created_at: string;
  };
  user_id: string;
  customer_id: string;
};

export type ManualCostCenterEntityType = "project";
export type ManualProjectRole = "project" | "reporting_group";

export type ManualCostCenterEntityEvent = {
  event_id: string;
  action: "create" | "update_role" | "archive" | string;
  cost_center_type: ManualCostCenterEntityType | string;
  cost_center_key: string;
  display_name: string;
  project_role?: ManualProjectRole | string | null;
  actor_user_id: string | null;
  note: string | null;
  created_at: string;
};

export type ManualCostCenterEntityResponse = {
  item: CostCenterEntity;
  event: ManualCostCenterEntityEvent;
};

export type CostCenterSummary = {
  entity_count: number | string | null;
  total_spend: number | string | null;
  spend_ytd: number | string | null;
  spend_last_year: number | string | null;
  spend_last30: number | string | null;
  credit_spend: number | string | null;
  credit_spend_last_year: number | string | null;
  document_count: number | string | null;
  line_count: number | string | null;
  user_count: number | string | null;
  with_pdf_evidence: number | string | null;
  with_ugl_evidence: number | string | null;
  with_manual_evidence: number | string | null;
  project_count: number | string | null;
  delivery_address_count: number | string | null;
  customer_reference_count: number | string | null;
  order_context_count: number | string | null;
  manual_cost_center_count: number | string | null;
};

export type CostCenterSourceCoverageSummary = {
  total: number | string | null;
  complete: number | string | null;
  review_needed: number | string | null;
  source_gap: number | string | null;
  unsupported: number | string | null;
  unknown: number | string | null;
  with_pdf_evidence: number | string | null;
  with_ugl_evidence: number | string | null;
  with_datanorm_evidence: number | string | null;
  with_manual_evidence: number | string | null;
  total_spend?: number | string | null;
  spend_ytd?: number | string | null;
  spend_last_year?: number | string | null;
  spend_last30?: number | string | null;
  credit_spend?: number | string | null;
  credit_spend_last_year?: number | string | null;
  document_count?: number | string | null;
  line_count?: number | string | null;
  pdf_document_count?: number | string | null;
  pdf_line_count?: number | string | null;
  ugl_document_count?: number | string | null;
  ugl_line_count?: number | string | null;
};

export type CostCenterListResponse = {
  total: number;
  limit: number;
  offset: number;
  summary: CostCenterSummary;
  items: CostCenterEntity[];
};

export type CostCenterMemberParent = {
  cost_center_type: CostCenterType | string;
  cost_center_key: string;
  display_label: string | null;
  total_spend: number | string | null;
  spend_last_year: number | string | null;
};

export type CostCenterMember = {
  cost_center_type: CostCenterType | string;
  cost_center_key: string;
  display_label: string | null;
  source_display_label?: string | null;
  manual_display_label?: string | null;
  total_spend: number | string | null;
  spend_last_year: number | string | null;
  document_count: number | string | null;
  assigned_at: string;
  assigned_by_user_id: string | null;
  note: string | null;
};

export type CostCenterMembersResponse = {
  parent: CostCenterMemberParent;
  members: CostCenterMember[];
  aggregate_spend: number | string | null;
  aggregate_spend_last_year: number | string | null;
};

// 4.6.A unified per-entity assignments — see
// docs/cost_center_assignments_contract.md. One list merges manual grouping
// members (origin "manual") and auto-confirmed/confirmed parser relationships
// (origin "auto"); sub-threshold "suggested" matches stay in `suggestions`.
export type CostCenterAssignmentOrigin = "manual" | "auto";

export type CostCenterAssignment = {
  origin: CostCenterAssignmentOrigin;
  cost_center_type: CostCenterType | string;
  cost_center_key: string;
  display_label: string | null;
  source_display_label?: string | null;
  manual_display_label?: string | null;
  spend_last_year: number | string | null;
  document_count: number | string | null;
  // origin === "manual" only
  assigned_at?: string | null;
  assigned_by_user_id?: string | null;
  note?: string | null;
  // origin === "auto" only (detail-on-demand)
  relationship_type?: string | null;
  state?: string | null;
  score?: number | null;
  reason?: string | null;
  source_summary?: string | null;
  parent_cost_center_type?: CostCenterType | string | null;
  parent_cost_center_key?: string | null;
  child_cost_center_type?: CostCenterType | string | null;
  child_cost_center_key?: string | null;
};

// Suggestions share the auto-assignment shape but carry no `origin`.
export type CostCenterAssignmentSuggestion = Omit<CostCenterAssignment, "origin">;

export type CostCenterAssignmentsResponse = {
  entity: {
    cost_center_type: CostCenterType | string;
    cost_center_key: string;
    display_label: string | null;
    source_display_label: string | null;
    manual_display_label: string | null;
  };
  aggregates: {
    own_spend_last_year: number | string | null;
    assigned_spend_last_year: number | string | null;
    evaluation_spend_last_year: number | string | null;
  };
  assignments: CostCenterAssignment[];
  suggestions: CostCenterAssignmentSuggestion[];
  suggestion_count: number;
};

export type CostCenterDisplayNameOverride = {
  cost_center_type: CostCenterType | string;
  cost_center_key: string;
  display_name: string;
  source_display_label: string | null;
  updated_at: string;
  source_event_id: string;
};

export type CostCenterDisplayNameEvent = {
  event_id: string;
  cost_center_type: CostCenterType | string;
  cost_center_key: string;
  action: "set" | "clear" | string;
  previous_display_name: string | null;
  new_display_name: string | null;
  source_display_label_at_event: string | null;
  actor_user_id: string | null;
  note: string | null;
  created_at: string;
};

export type CostCenterAlias = {
  source_cost_center_type: CostCenterType | string;
  source_cost_center_key: string;
  source_display_label: string | null;
  target_cost_center_type: CostCenterType | string;
  target_cost_center_key: string;
  target_display_label: string | null;
  updated_at: string;
  source_event_id: string;
};

export type CostCenterAliasEvent = {
  event_id: string;
  action: "merge" | "undo" | string;
  source_cost_center_type: CostCenterType | string;
  source_cost_center_key: string;
  source_display_label_at_event: string | null;
  target_cost_center_type: CostCenterType | string | null;
  target_cost_center_key: string | null;
  target_display_label_at_event: string | null;
  previous_target_cost_center_type: CostCenterType | string | null;
  previous_target_cost_center_key: string | null;
  previous_target_display_label_at_event: string | null;
  actor_user_id: string | null;
  note: string | null;
  created_at: string;
};

export type CostCenterManualEvent = {
  event_id: string;
  event_kind:
    | "display_name"
    | "manual_entity"
    | "cost_center_grouping"
    | "cost_center_alias"
    | "relationship_decision"
    | string;
  action: string;
  cost_center_type: CostCenterType | string | null;
  cost_center_key: string | null;
  display_label: string | null;
  source_display_label: string | null;
  manual_display_label: string | null;
  previous_display_name: string | null;
  new_display_name: string | null;
  target_cost_center_type: CostCenterType | string | null;
  target_cost_center_key: string | null;
  target_display_label: string | null;
  actor_user_id: string | null;
  note: string | null;
  created_at: string | null;
};

export type CostCenterDisplayNameResponse = {
  override?: CostCenterDisplayNameOverride;
  event: CostCenterDisplayNameEvent;
};

export type CostCenterManualEventListResponse = {
  items: CostCenterManualEvent[];
};

export type CostCenterAliasListResponse = {
  items: CostCenterAlias[];
};

export type CostCenterAliasResponse = {
  alias?: CostCenterAlias;
  event: CostCenterAliasEvent;
};

export type CostCenterSourceCoverageResponse = {
  total: number;
  limit: number;
  offset: number;
  summary: CostCenterSourceCoverageSummary;
  items: CostCenterSourceCoverage[];
};

export type CostCenterRelationshipListResponse = {
  total: number;
  limit: number;
  offset: number;
  stats: CostCenterRelationshipStatusStats;
  items: CostCenterRelationshipCandidate[];
};

export type CostCenterRelationshipEvidenceResponse = {
  total: number;
  limit: number;
  offset: number;
  items: CostCenterRelationshipEvidence[];
};

export async function fetchCostCenters(
  {
    type,
    q,
    sort,
    limit = 500,
    offset = 0,
  }: {
    type?: CostCenterType;
    q?: string;
    sort?: CostCenterSortMode;
    limit?: number;
    offset?: number;
  } = {},
): Promise<CostCenterListResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (type) params.set("type", type);
  if (q && q.trim()) params.set("q", q.trim());
  if (sort) params.set("sort", sort);
  return fetchJson<CostCenterListResponse>(`/api/cost-centers?${params.toString()}`);
}

export async function createManualCostCenterEntity({
  costCenterType,
  displayName,
  projectRole,
  members,
  note,
}: {
  costCenterType: ManualCostCenterEntityType;
  displayName: string;
  projectRole?: ManualProjectRole;
  members?: Array<{
    costCenterType: CostCenterType | string;
    costCenterKey: string;
    note?: string;
  }>;
  note?: string;
}): Promise<ManualCostCenterEntityResponse> {
  return fetchJson<ManualCostCenterEntityResponse>(
    "/api/cost-centers/manual-entities",
    {
      method: "POST",
      body: JSON.stringify({
        cost_center_type: costCenterType,
        display_name: displayName,
        project_role: projectRole,
        members: members?.map((member) => ({
          cost_center_type: member.costCenterType,
          cost_center_key: member.costCenterKey,
          note: member.note,
        })),
        note,
      }),
    },
  );
}

export async function updateManualProjectRole({
  costCenterType,
  costCenterKey,
  projectRole,
  note,
}: {
  costCenterType: CostCenterType | string;
  costCenterKey: string;
  projectRole: ManualProjectRole;
  note?: string;
}): Promise<ManualCostCenterEntityResponse> {
  return fetchJson<ManualCostCenterEntityResponse>(
    `/api/cost-centers/manual-entities/${encodeURIComponent(
      costCenterType,
    )}/${encodeURIComponent(costCenterKey)}/role`,
    {
      method: "POST",
      body: JSON.stringify({
        project_role: projectRole,
        note,
      }),
    },
  );
}

export async function archiveManualProject({
  costCenterType,
  costCenterKey,
  note,
  removeGroupings,
}: {
  costCenterType: CostCenterType | string;
  costCenterKey: string;
  note?: string;
  removeGroupings?: boolean;
}): Promise<ManualCostCenterEntityResponse> {
  return fetchJson<ManualCostCenterEntityResponse>(
    `/api/cost-centers/manual-entities/${encodeURIComponent(
      costCenterType,
    )}/${encodeURIComponent(costCenterKey)}/archive`,
    {
      method: "POST",
      body: JSON.stringify({ note, remove_groupings: removeGroupings ?? false }),
    },
  );
}

// Cost-center groupings — see 4.5.B contract. Backend endpoints
// `/api/cost-centers/{parent_type}/{parent_key}/members[/remove]` are
// implemented by codex; the client below assumes that shape and degrades
// gracefully (loading/empty/error states) while the backend ships.

export async function fetchCostCenterMembers({
  parentCostCenterType,
  parentCostCenterKey,
}: {
  parentCostCenterType: CostCenterType | string;
  parentCostCenterKey: string;
}): Promise<CostCenterMembersResponse> {
  return fetchJson<CostCenterMembersResponse>(
    `/api/cost-centers/${encodeURIComponent(
      parentCostCenterType,
    )}/${encodeURIComponent(parentCostCenterKey)}/members`,
  );
}

export async function fetchCostCenterAssignments({
  costCenterType,
  costCenterKey,
}: {
  costCenterType: CostCenterType | string;
  costCenterKey: string;
}): Promise<CostCenterAssignmentsResponse> {
  return fetchJson<CostCenterAssignmentsResponse>(
    `/api/cost-centers/${encodeURIComponent(
      costCenterType,
    )}/${encodeURIComponent(costCenterKey)}/assignments`,
  );
}

export type CostCenterSpendByMonthResponse = {
  items: ProjectSpendMonth[];
};

export async function fetchCostCenterSpendByMonth({
  costCenterType,
  costCenterKey,
  limit = 240,
  offset = 0,
}: {
  costCenterType: CostCenterType | string;
  costCenterKey: string;
  limit?: number;
  offset?: number;
}): Promise<CostCenterSpendByMonthResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return fetchJson<CostCenterSpendByMonthResponse>(
    `/api/cost-centers/${encodeURIComponent(
      costCenterType,
    )}/${encodeURIComponent(
      costCenterKey,
    )}/spend-by-month?${params.toString()}`,
  );
}

export async function addCostCenterMember({
  parentCostCenterType,
  parentCostCenterKey,
  memberCostCenterType,
  memberCostCenterKey,
  note,
}: {
  parentCostCenterType: CostCenterType | string;
  parentCostCenterKey: string;
  memberCostCenterType: CostCenterType | string;
  memberCostCenterKey: string;
  note?: string;
}): Promise<CostCenterMembersResponse> {
  return fetchJson<CostCenterMembersResponse>(
    `/api/cost-centers/${encodeURIComponent(
      parentCostCenterType,
    )}/${encodeURIComponent(parentCostCenterKey)}/members`,
    {
      method: "POST",
      body: JSON.stringify({
        cost_center_type: memberCostCenterType,
        cost_center_key: memberCostCenterKey,
        note,
      }),
    },
  );
}

export async function removeCostCenterMember({
  parentCostCenterType,
  parentCostCenterKey,
  memberCostCenterType,
  memberCostCenterKey,
  note,
}: {
  parentCostCenterType: CostCenterType | string;
  parentCostCenterKey: string;
  memberCostCenterType: CostCenterType | string;
  memberCostCenterKey: string;
  note?: string;
}): Promise<CostCenterMembersResponse> {
  return fetchJson<CostCenterMembersResponse>(
    `/api/cost-centers/${encodeURIComponent(
      parentCostCenterType,
    )}/${encodeURIComponent(parentCostCenterKey)}/members/remove`,
    {
      method: "POST",
      body: JSON.stringify({
        cost_center_type: memberCostCenterType,
        cost_center_key: memberCostCenterKey,
        note,
      }),
    },
  );
}

export async function setCostCenterDisplayName({
  costCenterType,
  costCenterKey,
  displayName,
  note,
}: {
  costCenterType: CostCenterType;
  costCenterKey: string;
  displayName: string;
  note?: string;
}): Promise<CostCenterDisplayNameResponse> {
  return fetchJson<CostCenterDisplayNameResponse>("/api/cost-centers/display-name", {
    method: "POST",
    body: JSON.stringify({
      cost_center_type: costCenterType,
      cost_center_key: costCenterKey,
      display_name: displayName,
      note,
    }),
  });
}

export async function clearCostCenterDisplayName({
  costCenterType,
  costCenterKey,
  note,
}: {
  costCenterType: CostCenterType;
  costCenterKey: string;
  note?: string;
}): Promise<CostCenterDisplayNameResponse> {
  return fetchJson<CostCenterDisplayNameResponse>(
    "/api/cost-centers/display-name/clear",
    {
      method: "POST",
      body: JSON.stringify({
        cost_center_type: costCenterType,
        cost_center_key: costCenterKey,
        note,
      }),
    },
  );
}

export async function fetchCostCenterManualEvents({
  limit = 100,
}: {
  limit?: number;
} = {}): Promise<CostCenterManualEventListResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  return fetchJson<CostCenterManualEventListResponse>(
    `/api/cost-centers/manual-events?${params.toString()}`,
  );
}

export async function fetchCostCenterAliases({
  costCenterType,
  costCenterKey,
}: {
  costCenterType?: CostCenterType | string;
  costCenterKey?: string;
} = {}): Promise<CostCenterAliasListResponse> {
  const params = new URLSearchParams();
  if (costCenterType && costCenterKey) {
    params.set("cost_center_type", costCenterType);
    params.set("cost_center_key", costCenterKey);
  }
  const query = params.toString();
  return fetchJson<CostCenterAliasListResponse>(
    `/api/cost-centers/aliases${query ? `?${query}` : ""}`,
  );
}

export async function mergeCostCenterAlias({
  sourceCostCenterType,
  sourceCostCenterKey,
  targetCostCenterType,
  targetCostCenterKey,
  note,
}: {
  sourceCostCenterType: CostCenterType | string;
  sourceCostCenterKey: string;
  targetCostCenterType: CostCenterType | string;
  targetCostCenterKey: string;
  note?: string;
}): Promise<CostCenterAliasResponse> {
  return fetchJson<CostCenterAliasResponse>("/api/cost-centers/aliases/merge", {
    method: "POST",
    body: JSON.stringify({
      source_cost_center_type: sourceCostCenterType,
      source_cost_center_key: sourceCostCenterKey,
      target_cost_center_type: targetCostCenterType,
      target_cost_center_key: targetCostCenterKey,
      note,
    }),
  });
}

export async function undoCostCenterAlias({
  sourceCostCenterType,
  sourceCostCenterKey,
  targetCostCenterType,
  targetCostCenterKey,
  sourceEventId,
  note,
}: {
  sourceCostCenterType: CostCenterType | string;
  sourceCostCenterKey: string;
  targetCostCenterType: CostCenterType | string;
  targetCostCenterKey: string;
  sourceEventId?: string;
  note?: string;
}): Promise<CostCenterAliasResponse> {
  return fetchJson<CostCenterAliasResponse>("/api/cost-centers/aliases/undo", {
    method: "POST",
    body: JSON.stringify({
      source_cost_center_type: sourceCostCenterType,
      source_cost_center_key: sourceCostCenterKey,
      target_cost_center_type: targetCostCenterType,
      target_cost_center_key: targetCostCenterKey,
      source_event_id: sourceEventId,
      note,
    }),
  });
}

export async function fetchCostCenter(
  costCenterKey: string,
  costCenterType?: CostCenterType | string | null,
): Promise<CostCenterEntity> {
  const params = new URLSearchParams({ key: costCenterKey });
  if (costCenterType) params.set("type", costCenterType);
  return fetchJson<CostCenterEntity>(
    `/api/cost-centers/detail?${params.toString()}`,
  );
}

export async function fetchCostCenterSourceCoverage(
  {
    type,
    q,
    view,
    status,
    gap,
    source,
    limit = 500,
    offset = 0,
  }: {
    type?: CostCenterType;
    q?: string;
    view?: "raw" | "customer_anchor";
    status?: CostCenterCoverageStatus | "all";
    gap?: string;
    source?: CostCenterCoverageSourceFilter;
    limit?: number;
    offset?: number;
  } = {},
): Promise<CostCenterSourceCoverageResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (type) params.set("type", type);
  if (q && q.trim()) params.set("q", q.trim());
  if (view && view !== "raw") params.set("view", view);
  if (status && status !== "all") params.set("status", status);
  if (gap && gap !== "all") params.set("gap", gap);
  if (source && source !== "all") params.set("source", source);
  return fetchJson<CostCenterSourceCoverageResponse>(
    `/api/cost-centers/source-coverage?${params.toString()}`,
  );
}

export async function fetchCostCenterSourceCoverageDetail(
  costCenterKey: string,
  costCenterType?: CostCenterType | string | null,
): Promise<CostCenterSourceCoverage> {
  const params = new URLSearchParams({ key: costCenterKey });
  if (costCenterType) params.set("type", costCenterType);
  return fetchJson<CostCenterSourceCoverage>(
    `/api/cost-centers/source-coverage/detail?${params.toString()}`,
  );
}

export async function fetchCostCenterLabelCandidates(
  costCenterKey: string,
  costCenterType?: CostCenterType | string | null,
): Promise<CostCenterLabelCandidatesResponse> {
  const params = new URLSearchParams({ key: costCenterKey });
  if (costCenterType) params.set("type", costCenterType);
  return fetchJson<CostCenterLabelCandidatesResponse>(
    `/api/cost-centers/label-candidates?${params.toString()}`,
  );
}

export async function fetchCostCenterRelationships(
  {
    parent,
    parentType,
    child,
    childType,
    relationshipType,
    q,
    status,
    source,
    minScore,
    sort,
    limit = 500,
    offset = 0,
  }: {
    parent?: string;
    parentType?: CostCenterType | string | null;
    child?: string;
    childType?: CostCenterType | string | null;
    relationshipType?: string;
    q?: string;
    status?: CostCenterRelationshipStatusFilter;
    source?: CostCenterRelationshipSourceFilter;
    minScore?: number;
    sort?: CostCenterRelationshipSortMode;
    limit?: number;
    offset?: number;
  } = {},
): Promise<CostCenterRelationshipListResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (parent) params.set("parent", parent);
  if (parentType) params.set("parent_type", parentType);
  if (child) params.set("child", child);
  if (childType) params.set("child_type", childType);
  if (relationshipType) params.set("relationship_type", relationshipType);
  if (q && q.trim()) params.set("q", q.trim());
  if (status && status !== "all") params.set("status", status);
  if (source && source !== "all") params.set("source", source);
  if (minScore && minScore > 0) params.set("min_score", String(minScore));
  if (sort) params.set("sort", sort);
  return fetchJson<CostCenterRelationshipListResponse>(
    `/api/cost-centers/relationships?${params.toString()}`,
  );
}

export async function fetchCostCenterRelationshipEvidence({
  candidate,
  limit = 50,
  offset = 0,
}: {
  candidate: CostCenterRelationshipCandidate;
  limit?: number;
  offset?: number;
}): Promise<CostCenterRelationshipEvidenceResponse> {
  const params = new URLSearchParams({
    parent: candidate.parent_cost_center_key,
    parent_type: candidate.parent_cost_center_type,
    child: candidate.child_cost_center_key,
    child_type: candidate.child_cost_center_type,
    relationship_type: candidate.relationship_type,
    limit: String(limit),
    offset: String(offset),
  });
  return fetchJson<CostCenterRelationshipEvidenceResponse>(
    `/api/cost-centers/relationships/evidence?${params.toString()}`,
  );
}

export async function decideCostCenterRelationship({
  candidate,
  action,
  reason,
}: {
  candidate: CostCenterRelationshipCandidate;
  action: CostCenterRelationshipDecisionAction;
  reason?: string;
}): Promise<CostCenterRelationshipDecisionResponse> {
  return fetchJson<CostCenterRelationshipDecisionResponse>(
    "/api/cost-centers/relationships/decision",
    {
      method: "POST",
      body: JSON.stringify({
        parent_cost_center_key: candidate.parent_cost_center_key,
        parent_cost_center_type: candidate.parent_cost_center_type,
        child_cost_center_key: candidate.child_cost_center_key,
        child_cost_center_type: candidate.child_cost_center_type,
        relationship_type: candidate.relationship_type,
        action,
        reason,
      }),
    },
  );
}

export async function createManualCostCenterRelationship({
  parentCostCenterKey,
  childCostCenterKey,
  reason,
}: {
  parentCostCenterKey: string;
  childCostCenterKey: string;
  reason?: string;
}): Promise<CostCenterRelationshipDecisionResponse> {
  return fetchJson<CostCenterRelationshipDecisionResponse>(
    "/api/cost-centers/relationships/manual",
    {
      method: "POST",
      body: JSON.stringify({
        parent_cost_center_key: parentCostCenterKey,
        child_cost_center_key: childCostCenterKey,
        reason,
      }),
    },
  );
}

export async function removeManualCostCenterRelationship({
  candidate,
  reason,
}: {
  candidate: CostCenterRelationshipCandidate;
  reason?: string;
}): Promise<CostCenterRelationshipDecisionResponse> {
  return fetchJson<CostCenterRelationshipDecisionResponse>(
    "/api/cost-centers/relationships/manual/remove",
    {
      method: "POST",
      body: JSON.stringify({
        parent_cost_center_key: candidate.parent_cost_center_key,
        parent_cost_center_type: candidate.parent_cost_center_type,
        child_cost_center_key: candidate.child_cost_center_key,
        child_cost_center_type: candidate.child_cost_center_type,
        relationship_type: candidate.relationship_type,
        reason,
      }),
    },
  );
}
